import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getAuthTransport } from '@/lib/api/authTransport';
import { apiFetch } from '@/lib/api/client';
import { runtimeConfig } from '@/lib/config';

/**
 * 안전교육 이수확인서 · 안전수칙 이행각서 QR (V547 · DOC-129).
 *
 * 목록 화면 쪽(로그인) — QR 칸 · [QR 관리] 창에서 링크 열기 · 닫기, 낸 사람 명단 · 개인별 PDF. 근로자 쪽(로그인 없음)은
 * `pages/sign/SafeSignPage.tsx` 가 `/v1/safe-sign/{token}` 을 직접 부른다.
 */

/** 일지의 링크. 화면이 `/sign/{token}` 주소를 지어 QR 로 그린다. */
export type SignLink = { token: string; expiresAt: string; open: boolean; submitted: number };

export type PledgeRow = {
  id: string;
  name: string;
  affiliation: string | null;
  trade: string | null;
  phone: string | null;
  submittedAt: string;
};

const base = (journalId: string) => `/v1/safe/journals/${journalId}`;

export const signKeys = {
  link: (journalId: string) => ['safe-sign', 'link', journalId] as const,
  pledges: (journalId: string) => ['safe-sign', 'pledges', journalId] as const,
};

/** 근로자가 찍을 주소 — 지금 보고 있는 사이트의 주소 위에 짓는다(사내 · 운영 어디서든 같은 화면이 열린다). */
export function signUrl(token: string): string {
  return `${window.location.origin}/sign/${token}`;
}

export function useSignLink(journalId: string | null) {
  return useQuery({
    queryKey: signKeys.link(journalId ?? ''),
    queryFn: () => apiFetch<SignLink | null>(`${base(journalId!)}/sign-link`),
    enabled: !!journalId,
    retry: false,
  });
}

/** 연다(open) · 닫는다(close). */
export function useSignLinkWrite(journalId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: 'open' | 'close') =>
      apiFetch<SignLink | null>(`${base(journalId!)}/sign-link`, { method: action === 'open' ? 'POST' : 'DELETE' }),
    onSuccess: (data) => {
      queryClient.setQueryData(signKeys.link(journalId ?? ''), data);
      void queryClient.invalidateQueries({ queryKey: ['safe-sign', 'board'] });
    },
  });
}

/** 목록 화면의 일지 한 장 — QR 칸과 [QR 관리] 창이 같이 읽는다. 연 적이 없으면 link 가 null. */
export type BoardRow = { journalId: string; link: SignLink | null; pledges: PledgeRow[] };

/** 보이는 일지들의 링크 · 명단. 열린 QR 이 있거나 관리 창이 떠 있으면 10초마다 다시 받는다. */
export function useSignBoard(journalIds: string[], live: boolean) {
  return useQuery({
    queryKey: ['safe-sign', 'board', journalIds] as const,
    queryFn: () => apiFetch<BoardRow[]>('/v1/safe/sign-board', { method: 'POST', body: { journalIds } }),
    enabled: journalIds.length > 0,
    refetchInterval: (query) => (live || query.state.data?.some((row) => row.link?.open) ? 10_000 : false),
    retry: false,
  });
}

/** 낸 사람 명단 — 근로자가 내는 것이 보이게 열려 있는 동안 10초마다 다시 받는다. */
export function usePledges(journalId: string | null, live: boolean) {
  return useQuery({
    queryKey: signKeys.pledges(journalId ?? ''),
    queryFn: () => apiFetch<PledgeRow[]>(`${base(journalId!)}/pledges`),
    enabled: !!journalId,
    refetchInterval: live ? 10_000 : false,
    retry: false,
  });
}

export function usePledgeDelete(journalId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pledgeId: string) => apiFetch<void>(`${base(journalId!)}/pledges/${pledgeId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['safe-sign'] }),
  });
}

/** 한 사람의 확인서 · 각서 PDF — 보던 탭 그대로 내려받는다. */
export async function downloadPledge(journalId: string, pledgeId: string, fallbackName: string): Promise<void> {
  const transport = getAuthTransport();
  const response = await fetch(`${runtimeConfig().apiBaseUrl}${base(journalId)}/pledges/${pledgeId}/export`, {
    credentials: transport.credentials,
    headers: transport.authHeaders({ unsafe: false }),
  });
  if (!response.ok) throw new Error(`파일을 만들지 못했습니다. (HTTP ${response.status})`);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = encoded ? decodeURIComponent(encoded) : `${fallbackName}.pdf`;
  link.click();
  URL.revokeObjectURL(link.href);
}
